import { flatMap, map, sortBy, toString, values } from 'es-toolkit/compat';

import { StoreType } from '@tock/shared/utils/connect';
import { consumer, core } from '@tock/coretypes';
import { createSelector } from 'reselect';
import { getOrCreate } from '@tock/shared/utils/dictUtils';
import { oc } from 'ts-optchain';
import { purchaseSummariesSelector } from 'selectors/purchaseHistorySelector';

export interface LocationPurchaseHistory {
  /** Location of this history item */
  location: google.maps.LatLngLiteral;
  /** Business for this item. Multiple businesses can have the same location,
   * and a single business can have multiple locations (due to events that take
   * place in alternate locations). */
  business: core.IBusinessPublic;
  /** All purchases associated with the given business at the given location. */
  purchases: consumer.PurchaseSummary[];
  /** For the given location, a unique index for the specified business starting
   * at zero and moving up by 1. The combination of location/business/index is
   * unique within this selector's output (though no subset of those keys is
   * guaranteed to be unique).
   */
  indexWithinLocation: number;
}

// Internal interface that represents location purchase history hierarchically
// in a tree structure by location and then business. The algorithm below works
// by building this tree structure and then flattening it into a list of
// LocationPurchaseHistory items to return.
interface LocationPurchaseHistoryInternal {
  location: google.maps.LatLngLiteral;
  purchasesByBusiness: { [businessId: number]: consumer.PurchaseSummary[] };
}

/**
 * Selector that groups all PastPurchases in purchase history by their
 * geographic location. Purchases for which we have no location information are
 * omitted from the result.
 *
 * This selector is intended to batch up all the reservations by location so
 * that in the profile travel log map we can render a single pin per location
 * rather than many.
 */
const locationGroupedPurchaseHistorySelector = createSelector(
  // we explicitly wrap purchaseHistorySelector so we can weaken its output type,
  // which seems to be necessary for getting the types right in testing
  // the resultFunc
  (state: StoreType): consumer.PurchaseSummary[] => purchaseSummariesSelector(state),
  (purchaseHistoryItems: consumer.PurchaseSummary[]): LocationPurchaseHistory[] => {
    const purchasesByLocation: Dictionary<LocationPurchaseHistoryInternal> = {};
    for (const purchase of purchaseHistoryItems) {
      if (purchase.cancelledOrRefunded) {
        continue;
      }
      const loc = location(purchase);
      const businessId = purchase.business?.id;
      if (loc && businessId) {
        const locString = `${loc.lat},${loc.lng}`;
        const purchasesForLoc = getOrCreate<LocationPurchaseHistoryInternal>(
          purchasesByLocation,
          locString,
          () => {
            return {
              location: loc,
              purchasesByBusiness: {},
            };
          }
        );
        const purchasesForBusiness = getOrCreate(
          purchasesForLoc.purchasesByBusiness,
          toString(businessId),
          () => []
        );
        purchasesForBusiness.push(purchase);
      }
    }
    return flatMap(values(purchasesByLocation), convertToLocationPurchaseHistories);
  }
);

function convertToLocationPurchaseHistories(
  locationPurchaseHistoryInternal: LocationPurchaseHistoryInternal
): LocationPurchaseHistory[] {
  // Pull the purchases out of the dictionary -- we don't need the key anymore
  const groupedPurchases: consumer.PurchaseSummary[][] = values(
    locationPurchaseHistoryInternal.purchasesByBusiness
  );
  // Now sort by business so that we will be able to give each business a stable
  // index in the next step. (We know that each batch of purchases has the same
  // business and that each array is nonempty because of how we construct
  // LocationPurchaseHistoryInternal objects.)
  const sortedGroupedPurchases: consumer.PurchaseSummary[][] = sortBy(
    groupedPurchases,
    (purchases) => purchases[0].business?.name
  );

  // Now convert the arrays into a list of LocationPurchaseHistory objects.
  // Since all these purchase groups have the same location, if there is more
  // than one purchase group we need to differentiate by `indexWithinLocation`.
  // The sort from the previous step guarantees that the index is stable.
  return map(sortedGroupedPurchases, (purchases, idx) => {
    // business will always be populated because if we have no business the
    // purchases would never have been grouped, but Typescript doesn't know that
    const business = purchases[0].business || {};
    return {
      location: locationPurchaseHistoryInternal.location,
      business: business,
      purchases,
      indexWithinLocation: idx,
    };
  });
}

/**
 * Returns the location, if available, for the given booking.
 */
function location(pastPurchase: core.IPastPurchase): google.maps.LatLngLiteral | undefined {
  const location = oc(pastPurchase).ticketType.ticketTypeDetails.location();
  if (location && location.lat && location.lng) {
    return {
      lat: location.lat,
      lng: location.lng,
    };
  }
  const business = oc(pastPurchase).business();
  if (business && business.addressLat && business.addressLng) {
    return {
      lat: business.addressLat,
      lng: business.addressLng,
    };
  }
  return undefined;
}

export const locationPurchaseStatsSelector = createSelector(
  purchaseSummariesSelector,
  (purchaseSummaries: consumer.PurchaseSummary[]) => {
    const validPurchases = purchaseSummaries.filter(
      (purchaseSummary) => !purchaseSummary.cancelledOrRefunded
    );
    const visitedCities = new Set<string>();
    const visitedCountries = new Set<string>();

    validPurchases.forEach((purchaseSummary) => {
      const city = purchaseSummary.city?.toLowerCase();
      const country = purchaseSummary.country?.toLowerCase();
      visitedCities.add(`${city}_${country}`);
      visitedCountries.add(country);
    });

    return {
      reservations: validPurchases.length,
      cities: visitedCities.size,
      countries: visitedCountries.size,
    };
  }
);

export default locationGroupedPurchaseHistorySelector;
